import React, { useEffect, useRef, useState, useMemo } from "react";
import { IonPage } from "@ionic/react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Tabs,
  Typography,
  Space,
  Row,
  Col,
  Form,
  Input,
  Breadcrumb,
  Result,
  Spin,
  notification,
  Card,
  Modal,
  Tag,
} from "antd";
import { InfoCircleFilled, Loading3QuartersOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import deepcopy from "deepcopy";
import moment from "moment";
import _ from "lodash";
import { showErrMsg } from "../../../utils/error";

//
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/GetStarted/SectionHeader";
import Name from "../../../components/General/Name";

//
import useAuthStore from "../../../states/useAuth";
import useWelcomeQuery from "../../../hooks/useQuery/useWelcome";
import useWelcome from "../../../hooks/useWelcome";
import Description from "../../../components/General/Description";
import PeriodPicker from "../../../components/General/PeriodPicker";
import dayjs from "../../../utils/day";
import useCompanyStore from "../../../states/useCompany";
import ItemSelectionDrawer from "../../../components/ItemSelectionDrawer";
import useProductQuery from "../../../hooks/useQuery/useProduct";
import VoucherDetailDrawer from "../../../components/Welcome/VoucherDetailDrawer";
import Number from "../../../components/General/Number";
import { formatProductWithAvailableQty, getWelcomeStatus } from "../../../utils/welcome";
import { formatTime } from "../../../utils/time";
import WelcomeStatus from "../../../components/Table/WelcomeStatus";
import PackList from "../../../components/Welcome/PackList";

const { Title, Text, Paragraph } = Typography;
const { confirm } = Modal;

const SingleWelcome = () => {
  const { id } = useParams(); // Get the :id parameter from the URL
  const history = useHistory();
  const location = useLocation();

  //
  const { getMaxShopExpiry } = useCompanyStore();
  const { auth, user } = useAuthStore();
  const { createWelcome, updateWelcome } = useWelcome();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  //Form
  const [welcomeForm] = Form.useForm();
  const [newWelcome, setNewWelcome] = useState();

  //
  const [isShowVoucherDrawer, setIsShowVoucherDrawer] = useState(false);
  const [isShowVoucherDetailDrawer, setIsShowVoucherDetailDrawer] = useState(false);

  //
  const [originalWelcome, setOriginalWelcome] = useState();
  const [welcome, setWelcome] = useState();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const isActionBtnDisabled =
    id == "new" ? !c.hasDiff(originalWelcome, newWelcome) : !originalWelcome || !c.hasDiff(originalWelcome, welcome);

  const now = useMemo(() => moment().toISOString(), []);

  const welcomeQueryKey = useMemo(() => ["/welcome"], []);
  const {
    data: welcomes = [],
    isFetching: isFetchingWelcomes,
    error: welcomesError,
    refetch: refetchWelcomes,
  } = useWelcomeQuery("welcomes/query", welcomeQueryKey, { "time.end": { $gt: now } }, true);

  const singleWelcomeQueryKey = useMemo(() => ["/welcome/:id", id], [id]);
  const {
    data: singleWelcome,
    isFetching: isFetchingSingleWelcome,
    error: welcomeError,
    refetch: refetchSingleWelcome,
  } = useWelcomeQuery(
    "welcome/query",
    singleWelcomeQueryKey,
    {
      id: id,
    },
    !!id && id !== "new",
    {
      staleTime: Infinity, // Prevents refetching on component remounts or when coming back to the page
      cacheTime: 0, // Keeps data fresh only until the component is unmounted
    }
  );

  // Set original and current promotion code whenever new data is fetched
  useEffect(() => {
    setOriginalWelcome(singleWelcome);
    setWelcome(singleWelcome);
  }, [singleWelcome]);

  //
  const welcomeEndTime = id == "new" ? newWelcome?.time?.end : welcome?.time?.end;
  const {
    data: products = [],
    isFetching: isFetchingProducts,
    refetch: refetchProducts,
  } = useProductQuery(
    {
      type: "voucher",
    },
    {
      enabled: !!welcomeEndTime, // Ensure the query is only enabled when welcomeEndTime is defined
    }
  );

  const filteredProducts = useMemo(() => {
    // Determine the activeWelcome based on the id
    let activeWelcomes;
    if (id === "new") {
      // Append `newWelcome` if `id == "new"`
      activeWelcomes = [...welcomes, newWelcome];
    } else {
      // Overwrite the `welcomes` element based on `welcome.id` if `id` is not "new"
      activeWelcomes = welcomes.map((existingWelcome) =>
        existingWelcome.id === welcome?.id ? welcome : existingWelcome
      );
    }

    // Map filtered products to include qty.available
    return products.map((product, i) => {
      // Inject qty.available using formatProductWithAvailableQty
      const productWithQty = formatProductWithAvailableQty(product, activeWelcomes);

      // Add a unique key and return the updated product
      return { ...productWithQty, key: i };
    });
  }, [products, welcomes, newWelcome, id, welcome]);

  const submitForm = async () => {
    //check form
    try {
      await welcomeForm.validateFields();
    } catch (error) {
      log.error(error);
      return notification.error({
        message: "error",
        description: "Some of the input fields are invalid. Please review and save again",
      });
    }

    let before = id == "new" ? newWelcome : welcome;

    if (!before?.packs?.length > 0) {
      return notification.error({ message: "At least one pack" });
    }

    for (let pack of before.packs) {
      if (!pack.products?.length > 0) {
        return notification.error({ message: "At least one voucher in each pack" });
      }
    }

    // Get the diff between originalWelcome and request
    let diff = c.getObjectDiff(originalWelcome, before);
    console.log("Changed fields:", diff);

    if (!diff) {
      return notification.error({ message: "no information changed" });
    }

    // axios
    try {
      setIsLoadingSubmit(true);

      let response;
      if (id === "new") {
        response = await createWelcome(diff);
      } else {
        response = await updateWelcome(before.id, diff);
      }

      // Update data
      setOriginalWelcome(response);
      setWelcome(response);

      // Show success notification
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
      });

      //redirect
      if (id === "new") {
        history.replace(`/merchantUser/welcome/${response.id}`);
      }
    } catch (error) {
      showErrMsg(error);
    } finally {
      setIsLoadingSubmit(false); // Ensure loading state is cleared
    }
  };

  const updateWelcomeData = (newData) => {
    log.debug(newData);

    const update = (prev) => {
      // Create a copy of the previous shop data
      let updated = _.merge({}, prev, newData);

      // overwrite
      if (newData.packs) {
        updated = { ...updated, packs: newData.packs };
      }

      log.debug(updated);
      return updated;
    };

    if (id == "new") {
      setNewWelcome(update);
    } else {
      setWelcome(update);
    }
  };

  return (
    <div style={{ margin: "0 auto" }}>
      <Spin
        spinning={isFetchingSingleWelcome}
        // percent="auto"
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {welcomeError ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        />
      ) : (
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <a
                    onClick={() => {
                      history.push("/merchantUser/welcome");
                    }}
                  >
                    List
                  </a>
                ),
              },
              {
                title: <a>{id == "new" ? "New Welcome" : "Campaign Detail"}</a>,
              },
            ]}
          />

          <PageHeader
            title={id == "new" ? "New Welcome" : "Campaign Detail"}
            isSticky
            actionGroup={
              <Space>
                <Button
                  size="large"
                  disabled={isActionBtnDisabled}
                  loading={isLoadingSubmit}
                  color="default"
                  variant="filled"
                  style={{ marginRight: "1.5rem", width: "7.5rem" }}
                  onClick={async () => {
                    //reset
                    if (id == "new") {
                      setNewWelcome(originalWelcome);
                    } else {
                      setWelcome(originalWelcome);
                    }
                  }}
                >
                  Reset
                </Button>

                <Button
                  size="large"
                  type="primary"
                  disabled={isActionBtnDisabled}
                  loading={isLoadingSubmit}
                  style={{ width: "7.5rem" }}
                  onClick={() => {
                    submitForm();
                  }}
                >
                  {id == "new" ? "Create" : "Update"}
                </Button>
              </Space>
            }
            onBack={() => {
              history.goBack();
            }}
          />

          <Form form={welcomeForm} style={{ width: "100%", marginTop: "2rem" }}>
            <Row gutter={[16, 16]}>
              <Col span={16}>
                <Row align="top" style={{}}>
                  <Col span={4} style={{ fontSize: "1rem" }}>
                    Name
                  </Col>
                  <Col span={20}>
                    <Name
                      state={{
                        data: id == "new" ? newWelcome?.name : welcome?.name,
                        form: welcomeForm,
                        keyName: "name",
                        rules: [{ required: true, message: "required" }],
                        onChange: ({ name }) => updateWelcomeData({ name: name }),
                      }}
                      readOnly={false}
                      maxLength={60}
                      placeholder="Enter Name, this will not display on mobile"
                      showCount
                      style={{ width: "100%" }}
                    />
                  </Col>
                </Row>
                <Row align="top" style={{ marginBottom: "2rem" }}>
                  <Col span={4} style={{ fontSize: "1rem" }}>
                    Description
                  </Col>
                  <Col span={20}>
                    <Description
                      state={{
                        data: id == "new" ? newWelcome?.description : welcome?.description,
                        form: welcomeForm,
                        keyName: "description",
                        onChange: updateWelcomeData,
                      }}
                      readOnly={false}
                      placeholder={"optional, this will not display on mobile"}
                      row={2}
                      draggable={false}
                    />
                  </Col>
                </Row>

                {id == "new" && !newWelcome?.time ? null : (
                  <Packs
                    state={{
                      data: id === "new" ? newWelcome?.packs : welcome?.packs,
                      form: welcomeForm,
                    }}
                    products={filteredProducts}
                    welcomeEndTime={welcomeEndTime}
                    canEditPack={id == "new" || (id != "new" && getWelcomeStatus(welcome) == "pending")}
                    isFetchingProducts={isFetchingProducts}
                    isShowVoucherDrawer={isShowVoucherDrawer}
                    isShowVoucherDetailDrawer={isShowVoucherDetailDrawer}
                    setIsShowVoucherDrawer={setIsShowVoucherDrawer}
                    setIsShowVoucherDetailDrawer={setIsShowVoucherDetailDrawer}
                    isLoadingSubmit={isLoadingSubmit}
                    onAddPack={(qty) => {
                      const existing = id === "new" ? newWelcome?.packs ?? [] : welcome?.packs ?? [];
                      const updatedPacks = [...existing, { id: c.generateId(4, 4), qty: qty, products: [] }];
                      updateWelcomeData({ packs: updatedPacks });
                    }}
                    onDeletePack={(packId) => {
                      const existing = id === "new" ? newWelcome?.packs ?? [] : welcome?.packs ?? [];
                      const updatedPacks = existing.filter((pack) => pack.id !== packId);
                      updateWelcomeData({ packs: updatedPacks });
                    }}
                    onSaveVoucher={(updated) => {
                      updateWelcomeData({ packs: updated });
                      setIsShowVoucherDetailDrawer(false);
                    }}
                    onPackQtyChange={(packId, qty) => {
                      const existing = id === "new" ? newWelcome?.packs ?? [] : welcome?.packs ?? [];
                      const updatedData = existing.map((pack) => (pack.id === packId ? { ...pack, qty: qty } : pack));
                      updateWelcomeData({ packs: updatedData });
                    }}
                  />
                )}
              </Col>
              <Col span={8}>
                <Card
                  title={
                    <SectionHeader
                      title="Game Rules"
                      description="Each user can claim 1 pack randomly after successful registration within the specified period. It's first come, first served. You can update the quota for each pack at any time."
                      style={{
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                      }}
                    />
                  }
                  style={{
                    border: "2px solid var(--ion-color-step-200)",
                  }}
                >
                  <Row align="top" style={{ marginTop: "1rem" }}>
                    <Col span={24} style={{ marginRight: "1rem", fontSize: "1rem" }}>
                      Valid Period
                    </Col>
                    <Col span={24}>
                      {id == "new" ? (
                        <PeriodPicker
                          state={{
                            data: id === "new" ? newWelcome?.time : welcome?.time,
                            form: welcomeForm,
                            rules: [{ required: true, message: "required" }],
                            onChange: (updated) => {
                              const currentTime = (id === "new" ? newWelcome : welcome)?.time;

                              const hasExistingPeriod = currentTime?.start && currentTime?.end;

                              if (hasExistingPeriod) {
                                Modal.confirm({
                                  title: "Confirm?",
                                  content:
                                    "Changing the period will reset the selected products. Do you want to continue?",
                                  okText: "Yes",
                                  cancelText: "No",
                                  onOk: () => {
                                    updateWelcomeData({
                                      time: {
                                        ...currentTime,
                                        start: updated.start,
                                        end: updated.end,
                                      },
                                      packs: []
                                    });
                                  },
                                  onCancel: () => {
                                    // Revert UI back to old value
                                    welcomeForm.setFieldsValue({
                                      collectPeriod: [dayjs(currentTime.start), dayjs(currentTime.end)],
                                    });
                                  },
                                });
                              } else {
                                // no existing period, update immediately without confirmation
                                updateWelcomeData({
                                  time: {
                                    ...currentTime,
                                    start: updated.start,
                                    end: updated.end,
                                  },
                                });
                              }
                            },
                            keyName: "collectPeriod",
                          }}
                          minDate={dayjs()}
                          occupiedDates={welcomes.map((w) => ({
                            start: w.time.start,
                            end: w.time.end,
                          }))}
                          style={{ width: "100%" }}
                        />
                      ) : !welcome?.time ? null : (
                        <Text strong>
                          <WelcomeStatus welcome={welcome} style={{ marginRight: "1rem" }} />
                          {`${formatTime(welcome.time.start, "YYYY-MM-DD")} to ${formatTime(
                            welcome.time.end,
                            "YYYY-MM-DD"
                          )}`}
                        </Text>
                      )}
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </div>
  );
};

const Packs = ({
  state = {},
  products = [],
  welcomeEndTime,
  canEditPack,
  isFetchingProducts,
  isShowVoucherDrawer,
  isShowVoucherDetailDrawer,
  setIsShowVoucherDrawer,
  setIsShowVoucherDetailDrawer,
  isLoadingSubmit,
  onAddPack,
  onDeletePack,
  onSaveVoucher,
  onPackQtyChange,
}) => {
  const { data = [], form } = state; // `data` now refers to `packs`
  const { id } = useParams(); // Get the :id parameter from the URL
  //
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState();
  const [voucher, setVoucher] = useState();

  // Add New Pack
  const showAddPackModal = () => {
    let packQty = 0; // Local variable to store the number input

    const handleChange = (e) => {
      packQty = e; // Update the value as the user types
    };

    confirm({
      title: "How many quota for this pack?",
      icon: <InfoCircleFilled style={{ color: "var(--ion-color-primary)" }} />,
      content: <Number size="large" onChange={handleChange} placeholder="Enter a number" autoFocus />,
      onOk() {
        if (!packQty > 0) {
          notification.warning({ message: "Must be at least 1" });
          return Promise.reject(); // Prevent modal from closing
        }
        onAddPack(packQty);
      },
      onCancel() {
        console.log("Cancelled");
      },
    });
  };

  // For Voucher Selection Drawer
  const productsToSelect = useMemo(() => {
    // If `selectedPackId` is set, find the pack with that ID
    const selectedPack = data.find((pack) => pack.id === selectedPackId);

    // If a pack is found and it contains products, extract those products
    const existingProductIds = selectedPack
      ? selectedPack.products.map((p) => p.id) // Get product IDs from the selected pack
      : [];

    // Filter products based on availability and exclude already selected ones from the selected pack
    return products.filter(
      (e) =>
        e?.qty?.available > 0 &&
        !existingProductIds.includes(e.id) &&
        !e.isReferral &&
        moment(e?.time?.redeem?.end).isSameOrAfter(moment(welcomeEndTime))
    );
  }, [data, products, selectedPackId]);

  // For Voucher Selection Drawer
  const onSelectChange = (productIds) => {
    setSelectedProductIds(productIds); // Update local state
  };

  // For Voucher Detail Drawer
  const onConfirm = () => {
    const defaultVoucher = {
      _id: products.find((e) => e.id == selectedProductIds[0])?._id,
      id: selectedProductIds[0],
      qtyPerCollection: 0,
    };

    setVoucher(defaultVoucher);
    setIsShowVoucherDrawer(false);
    setIsShowVoucherDetailDrawer(true);
  };

  // For Voucher Detail Drawer
  const product = useMemo(() => {
    return selectedProductIds.length > 0 ? products.find((e) => e.id === selectedProductIds[0]) : undefined;
  }, [voucher, selectedProductIds, products]);

  const populatedPacks = useMemo(() => {
    // Map over the packs and inject product details into each pack's products
    return data.map((pack) => ({
      ...pack, // Keep the pack's properties
      products: pack.products.map((product) => {
        const matchedProduct = products.find((prod) => prod.id === product.id);
        return {
          ...product, // Retain existing product properties from the pack
          ...matchedProduct, // Inject matching product details
        };
      }),
    }));
  }, [products, data]); // Recalculate whenever products or data changes

  log.debug(populatedPacks);

  return (
    <>
      <ItemSelectionDrawer
        state={{
          data: selectedProductIds,
          form: form,
          onChange: onSelectChange,
        }}
        header={
          <SectionHeader
            title="Select Voucher"
            description="Only voucher can be selected"
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        items={productsToSelect}
        isShow={isShowVoucherDrawer}
        isLoading={isLoadingSubmit}
        isSingleSelect={true}
        tableType="product"
        confirmBtnText={"Next"}
        isConfirmBtnDisabled={selectedProductIds.length === 0}
        onClose={() => {
          setIsShowVoucherDrawer(false);
        }}
        onConfirm={onConfirm}
      />

      <VoucherDetailDrawer
        open={isShowVoucherDetailDrawer}
        isLoading={isLoadingSubmit}
        voucher={voucher}
        product={product}
        vouchers={data}
        packQty={data?.find((e) => e.id == selectedPackId)?.qty}
        header={
          <SectionHeader
            title="Voucher Detail"
            description={"Provide detail of the voucher"}
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        confirmBtnText="Submit"
        onClose={() => {
          setVoucher();
          setIsShowVoucherDetailDrawer(false);
        }}
        onConfirm={(updatedVoucher) => {
          log.debug(updatedVoucher);

          const packIndex = data.findIndex((pack) => pack.id == selectedPackId);
          const updatedData = deepcopy(data);
          if (packIndex !== -1) {
            const productIndex = updatedData[packIndex].products.findIndex((p) => p.id === updatedVoucher.id);
            if (productIndex !== -1) {
              updatedData[packIndex].products[productIndex] = updatedVoucher;
            } else {
              updatedData[packIndex].products.push(updatedVoucher);
            }
          }

          setSelectedProductIds([]);
          onSaveVoucher(updatedData);
        }}
      />

      <Card
        title={<SectionHeader title="Add Pack" description="At least 1 pack" />}
        style={{
          margin: "2rem 0 5rem 0",
          border: "2px solid var(--ion-color-step-200)",
        }}
        extra={
          <Button
            size="large"
            type="primary"
            disabled={!canEditPack}
            loading={isFetchingProducts}
            onClick={showAddPackModal}
          >
            + Add Pack
          </Button>
        }
      >
        <PackList
          state={{
            data: populatedPacks,
          }}
          canEdit={canEditPack}
          onDelete={onDeletePack}
          onAdd={(packId) => {
            setSelectedPackId(packId);
            setSelectedProductIds([]);
            setIsShowVoucherDrawer(true);
          }}
          onEditVoucher={(packId, productId) => {
            setSelectedPackId(packId);
            let selectedVoucher = data.find((e) => e.id == packId)?.products.find((e) => e.id == productId);
            setSelectedProductIds([selectedVoucher?.id]);
            setVoucher(selectedVoucher);
            setIsShowVoucherDetailDrawer(true);
          }}
          onPackQtyChange={onPackQtyChange}
        />
      </Card>
    </>
  );
};

export default SingleWelcome;
